import { useStableObject } from '@atlassian/post-office-context';
import { useAISmartButtonActions } from '@atlassian/ai-smart-button-api';
import { useEffect } from 'react';

import type {
	HandleActionType,
	ConversationAction,
} from '@atlassian/rovo-conversation-actions/types';

/**
 * A nudge action that extends `ConversationAction` with additional fields
 * used by the Rovo Smart Button's nudge system.
 *
 * Message makers use these fields to control how their nudge appears and behaves:
 *
 * - `title` — Display text shown in the nudge pill and sidebar
 * - `description` — Supporting text for the nudge
 * - `icon` — A React component factory that returns the icon to show
 * - `handleType` — `HandleActionType.Chat` opens Rovo Chat; `HandleActionType.Base` uses a custom handler
 * - `staticPrompt` — The prompt sent to Rovo Chat when the nudge is clicked (Chat mode)
 * - `starterType` — `'static'` for fixed prompts, `'dynamic'` for context-aware prompts
 * - `nudgeContext` — Contains the `id` used for backend matching in migrated mode
 */
export type RovoNudgeAction = Omit<ConversationAction, 'id' | 'handleType'> & {
	handleType: HandleActionType;
	/** The prompt sent to Rovo Chat when the nudge is clicked (only used with HandleActionType.Chat). */
	staticPrompt?: string;
	/** Whether the prompt is fixed ('static') or generated at runtime ('dynamic'). */
	starterType?: 'static' | 'dynamic';
	/** Backend matching context — the `id` must match a `feature_id` from the convo-ai `/proactive` endpoint. */
	nudgeContext?: {
		id: string;
	};
};

/**
 * Props for the `NudgeDefinition` headless component.
 *
 * Includes all `RovoNudgeAction` fields plus an optional `id` override.
 *
 * The `ConversationAction.id` sent to the Smart Button is a **stable** reporting identifier:
 * `post-office-rovo-nudge`.
 * Dynamic instance data is sent separately on `nudgeId` (`messageTemplateId`).
 */
export type NudgeDefinitionProps = RovoNudgeAction;

/**
 * Headless component used by rovo-nudge message templates to declare how they should appear
 * in the Rovo Smart Button. This component renders nothing to the DOM — instead it calls
 * `setNudges()` directly from the message template render tree.
 *
 * The surrounding rovo-nudge placement is only responsible for rendering the top backend-ranked
 * message template. Each rendered template owns the full lifecycle of its nudge registration.
 * This relies on the backend providing a single active nudge candidate at the top of the
 * subscription response order.
 *
 * @example
 * ```tsx
 * import { NudgeDefinition } from '@post-office/placements--post-office-team/rovo-nudge/nudge-definition';
 * import { HandleActionType } from '@atlassian/rovo-conversation-actions/types';
 * import SparkleIcon from '@atlaskit/icon/core/sparkle';
 *
 * const MessageFrontend = PlacementFrontend.createMessageFrontend({
 *   render: {
 *     Component: ({ title, description }) => {
 *       const icon = useCallback(() => <SparkleIcon label={title} />, [title]);
 *       return (
 *         <NudgeDefinition
 *           title={title}
 *           description={description}
 *           icon={icon}
 *           handleType={HandleActionType.Chat}
 *           staticPrompt={`Summarize: ${title}`}
 *           starterType="static"
 *           nudgeContext={{ id: 'my-feature-id' }}
 *         />
 *       );
 *     },
 *   },
 * });
 * ```
 */
export function NudgeDefinition(actionProps: NudgeDefinitionProps): null {
	const { setNudges } = useAISmartButtonActions();
	const stableActionProps = useStableObject(actionProps);

	useEffect(() => {
		// `RovoNudgeAction` is a superset of `ConversationAction` with additional nudge-specific
		// fields (for example `staticPrompt`, `starterType`, and `nudgeContext`) that are passed
		// through to the Smart Button API at runtime.
		setNudges([{ id: 'post-office-rovo-nudge', ...stableActionProps }]);

		// This simplified flow assumes only one rovo nudge is rendered at a time.
		// Clearing on unmount allows the next backend-ranked message to take over.
		return () => setNudges([]);
	}, [setNudges, stableActionProps]);

	return null;
}
